import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatsBar from "./components/StatsBar";
import UpdateLibrary from "./components/UpdateLibrary";
import AssignmentPanel from "./components/AssignmentPanel";
import AssignmentHistoryTable from "./components/AssignmentHistoryTable";
import UploadModal from "./components/UploadModal";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { sendEmailIfEnabled, getSystemSetting } from "../../lib/emailService";


const UpdateManagement = () => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUpdates = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('updates')?.select('*, update_assignments(id)')?.order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || [])?.map(u => ({
        id: u?.id,
        name: u?.name,
        version: u?.version,
        uploadDate: new Date(u.created_at)?.toLocaleDateString('es-CO'),
        fileSize: u?.file_size ? `${(u?.file_size / 1024 / 1024)?.toFixed(1)} MB` : '—',
        assignedCount: u?.update_assignments?.length || 0,
        status: u?.status?.toLowerCase() || 'active',
        _raw: u,
      }));

      setUpdates(mapped);
    } catch (err) {
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('update_assignments')?.select('*, updates(name, version), organizations(razon_social)')?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;

      const mapped = (data || [])?.map(a => ({
        id: a?.id,
        updateName: a?.updates?.name || '—',
        version: a?.updates?.version || '—',
        userName: a?.organizations?.razon_social || '—',
        org: a?.organizations?.razon_social || '—',
        assignedDate: new Date(a.created_at)?.toLocaleDateString('es-CO'),
        expiresDate: a?.signed_url_expires_at ? new Date(a.signed_url_expires_at)?.toLocaleDateString('es-CO') : '—',
        downloads: a?.download_count || 0,
        limit: a?.download_limit || 0,
        status: a?.status?.toLowerCase() || 'active',
        urlExpired: a?.signed_url_expires_at ? new Date(a.signed_url_expires_at) < new Date() : false,
        signedUrl: a?.signed_url,
        _raw: a,
      }));

      setAssignments(mapped);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
    fetchAssignments();
  }, [fetchUpdates, fetchAssignments]);

  const handleUpload = async (data) => {
    try {
      // File already uploaded to storage by UploadModal, just insert DB record
      const { data: insertData, error } = await supabase?.from('updates')?.insert({
        name: data?.name,
        version: data?.version,
        description: data?.description || null,
        file_path: data?.storagePath || null,
        file_size: data?.file?.size || null,
        file_name: data?.file?.name || null,
        created_by: userProfile?.id,
        status: 'ACTIVE',
      })?.select()?.single();

      if (error) throw error;

      await supabase?.from('audit_logs')?.insert({
        actor_id: userProfile?.id,
        action: 'UPDATE_UPLOADED',
        target_type: 'update',
        metadata: { name: data?.name, version: data?.version },
      });

      setShowUploadModal(false);
      showSuccess("Actualización cargada exitosamente");
      await fetchUpdates();
    } catch (err) {
      showSuccess("Error al registrar la actualización: " + (err?.message || ''));
    }
  };

  const handleAssign = async (orgIds, config) => {
    if (!selectedUpdate) return;

    try {
      // Read TTL from system settings (hours)
      const ttlHours = Number(config?.expHours || await getSystemSetting('signed_url_ttl_hours') || 24);
      const ttlSeconds = ttlHours * 3600;
      const expDate = new Date();
      expDate?.setTime(expDate?.getTime() + ttlSeconds * 1000);

      // Generate signed URL if file exists
      let signedUrl = null;
      if (selectedUpdate?._raw?.file_path) {
        const { data: urlData } = await supabase?.storage
          ?.from('updates')
          ?.createSignedUrl(selectedUpdate?._raw?.file_path, ttlSeconds);
        signedUrl = urlData?.signedUrl || null;
      }

      const downloadLimit = Number(config?.dlLimit) || 0;

      const insertRows = orgIds?.map(orgId => ({
        update_id: selectedUpdate?.id,
        organization_id: orgId,
        assigned_by: userProfile?.id,
        signed_url: signedUrl,
        signed_url_expires_at: expDate?.toISOString(),
        download_limit: downloadLimit > 0 ? downloadLimit : null,
        download_count: 0,
        status: 'ACTIVE',
      }));

      const { error } = await supabase?.from('update_assignments')?.insert(insertRows);
      if (error) throw error;

      await supabase?.from('audit_logs')?.insert({
        actor_id: userProfile?.id,
        action: 'UPDATE_ASSIGNED',
        target_type: 'update',
        metadata: { update_name: selectedUpdate?.name, org_count: orgIds?.length },
      });

      // Send email notifications to org contacts
      for (const orgId of orgIds) {
        try {
          const { data: orgData } = await supabase
            ?.from('organizations')
            ?.select('email, razon_social')
            ?.eq('id', orgId)
            ?.single();
          if (orgData?.email) {
            await sendEmailIfEnabled({
              toggleKey: 'notify_update_assigned',
              to: orgData?.email,
              template: 'actualizacion_asignada',
              variables: {
                razon_social: orgData?.razon_social || '',
                nombre_actualizacion: selectedUpdate?.name,
                version: selectedUpdate?.version,
                ttl_horas: String(ttlHours),
              },
            });
          }
        } catch (emailErr) {
        }
      }

      showSuccess(`Actualización asignada a ${orgIds?.length} organización(es) exitosamente`);
      await fetchUpdates();
      await fetchAssignments();
    } catch (err) {
      showSuccess("Error al asignar la actualización: " + (err?.message || ''));
    }
  };

  const handleRegenerateUrl = async (assignmentId, filePath) => {
    if (!assignmentId) {
      // null means just refresh
      await fetchAssignments();
      return;
    }
    try {
      const ttlHours = Number(await getSystemSetting('signed_url_ttl_hours') || 24);
      const ttlSeconds = ttlHours * 3600;
      const expDate = new Date();
      expDate?.setTime(expDate?.getTime() + ttlSeconds * 1000);

      let signedUrl = null;
      if (filePath) {
        const { data: urlData } = await supabase?.storage
          ?.from('updates')
          ?.createSignedUrl(filePath, ttlSeconds);
        signedUrl = urlData?.signedUrl || null;
      }

      await supabase?.from('update_assignments')?.update({
        signed_url: signedUrl,
        signed_url_expires_at: expDate?.toISOString(),
        status: 'ACTIVE',
      })?.eq('id', assignmentId);

      showSuccess("URL firmada regenerada exitosamente");
      await fetchAssignments();
    } catch (err) {
      showSuccess("Error al regenerar la URL");
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const mainMargin = sidebarCollapsed ? "lg:ml-[60px]" : "lg:ml-[240px]";
  const currentUser = {
    name: userProfile?.full_name || 'Operador',
    role: userProfile?.role || 'OPERATOR',
    organization: 'Update-vital-Ftech',
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <RoleBasedSidebar user={currentUser} notifications={{}} isCollapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} onLogout={handleLogout} />
      <div className={`transition-all duration-250 ${mainMargin} pt-16 lg:pt-0`}>
        {/* Vital branded sticky header */}
        <div className="sticky top-0 z-10 px-4 md:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ backgroundColor: 'var(--color-card)', borderBottom: '2px solid var(--color-primary)', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
          <div className="flex items-center gap-3 pl-10 lg:pl-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#00BCD4' }}>
              <Icon name="RefreshCw" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl md:text-2xl" style={{ color: "var(--color-foreground)" }}>Gestión de Actualizaciones</h1>
              <p className="font-caption text-xs" style={{ color: "var(--color-primary)" }}>Cargue, asigne y monitoree la distribución de paquetes de software</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate("/administrative-dashboard")} style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}>Reportes</Button>
            <Button variant="default" size="sm" iconName="Upload" iconPosition="left" onClick={() => setShowUploadModal(true)} style={{ backgroundColor: '#00BCD4', borderColor: '#00BCD4' }}>Cargar Actualización</Button>
          </div>
        </div>

        <div className="p-4 md:p-6 lg:p-8 space-y-5 md:space-y-6">
          {successMsg && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border" style={{ background: "rgba(0,188,212,0.08)", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}>
              <Icon name="CheckCircle" size={18} color="var(--color-primary)" />
              <span className="font-caption text-sm font-medium">{successMsg}</span>
            </div>
          )}

          <StatsBar />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-5" style={{ minHeight: "520px" }}>
            <div className="lg:col-span-2 h-[420px] lg:h-auto">
              <UpdateLibrary updates={updates} selectedUpdate={selectedUpdate} onSelectUpdate={setSelectedUpdate} onUploadClick={() => setShowUploadModal(true)} />
            </div>
            <div className="lg:col-span-3 h-[520px] lg:h-auto">
              <AssignmentPanel selectedUpdate={selectedUpdate} onAssign={handleAssign} />
            </div>
          </div>

          <AssignmentHistoryTable newAssignments={assignments} onRegenerateUrl={handleRegenerateUrl} />

          <footer className="text-center pt-2 pb-4">
            <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>&copy; {new Date()?.getFullYear()} Update-vital-Ftech — Distribución de Software Colombia.</p>
          </footer>
        </div>
      </div>

      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} onUpload={handleUpload} />
      )}
    </div>
  );
};

export default UpdateManagement;